<template>
	<div>
		<v-layout wrap pb-5>
			<v-flex xs8 pr-5>
				<tabs :items="items" :tabs="number" @change-tab="onChangeTab" />
				<component
					:is="componentName"
					:areas="areas"
					:local-factors="localFactors"
					:questions="questions"
					@on-active="onActive"
					@quantity-selected="quantitySelected"
					@row-check-select="rowCheckSelect"
					@select-factor="selectFactor"
					@show-questions="showQuestions"
					@get-disabled="getDisabled"
					@back-to="backTo"
				/>
			</v-flex>
			<v-flex xs4 class="page-container">
				<graphic-factors
					class="graphicFac"
					:number="number"
					:select-factors="selectFactors"
					:show-step="number === 1 || (number === 3 && showStep)"
					:title="title"
					:show-number="showNumber"
					:disabled-questions="disabledQuestions"
					:disabled-weighing="disabledWeighing"
					:rotated-card="rotatedCard"
					:cancel-action-text="cancelText"
					:success-action-text="successText"
					@cancel-action="backTo"
					@success-action="successAction"
				>
					<template v-if="isStep(1)">
						<template v-if="!rotatedCard">
							<graphic-component
								v-for="area in areas"
								:key="area.id"
								:area-id="area.id"
								:areas="areas"
								:graphic-value-left="graphicValueLeft"
								:graphic-value-left-second="graphicValueLeftSecond"
								:graphic-value-right="graphicValueRight"
								:graphic-value-right-second="graphicValueRightSecond"
								:title="area.name"
								:kind-entity="area.kindEntityData"
								:kind-entity-id="kindEntityId"
								:value="getAreaValues(area)"
								:disabled="getAreaDisabled(area)"
							/>
						</template>
						<template v-else>
							<card-detail :title="factorSelected.name" :description="factorSelected.description" />
						</template>
					</template>
					<template v-else-if="isStep(2)">
						<component-weighing
							:rows="localFactors.filter(f => f.kindEntity.hierarchyId === areas[0].id)"
							:rows-invested="localFactors.filter(f => f.kindEntity.hierarchyId === areas[1].id)"
							:title="areas[0].name"
							:title-invested="areas[1].name"
						/>
					</template>
					<template v-else-if="isStep(3)">
						<template v-if="!rotatedCard">
							<questions-detail :areas="areas" :local-factors="localFactors" />
						</template>
						<template v-else>
							<questions-list
								:questions="questionsByFactor"
								:quantity="quantity"
								:name="nameFactor"
								:title="titleQuestions"
								@on-change="onChange"
							></questions-list>
						</template>
					</template>
				</graphic-factors>
			</v-flex>
		</v-layout>
		<open-modal :dialog="dialog" :footer="false" @close-modal="closeModal">
			<v-layout justify-space-between pl-5 pb-5 pt-5>
				<v-flex xs8 pl-5 class="message-error-container">
					<label class="new-survey-label">Nombre de la Encuesta</label>
					<input
						class="new-survey-input"
						placeholder="Nombre de la Encuesta"
						:style="`color: ${$appColors.info} !important`"
						v-model="nameSurvey"
					/>
					<span v-if="!nameSurvey" class="message-error" :style="`color:${$appColors.error}`">
						El nombre es requerido
					</span>
				</v-flex>
				<v-flex xs2 pt-4>
					<v-btn
						class="btn btn-action btn-disabled-background"
						color="primary"
						:disabled="!nameSurvey"
						@click="createSurvey"
					>
						Finalizar
					</v-btn>
				</v-flex>
			</v-layout>
		</open-modal>
	</div>
</template>
<script>
import cardDetail from '@/components/shared/card-detail';
import componentWeighing from '@/components/surveys/component-weighing';
import graphicComponent from '@/components/surveys/graphic-component';
import graphicFactors from '@/components/surveys/graphic-factors';
import helper from '@/shared/helper';
import openModal from '@/components/configuration/open-modal';
import questionsDetail from '@/components/surveys/questions-detail';
import questionsList from '@/components/surveys/questions-list';
import stepOne from '@/components/surveys/step-one';
import stepThree from '@/components/surveys/step-three';
import stepTwo from '@/components/surveys/step-two';
import tabs from '@/components/surveys/tabs';
import toolsCodes from '@/shared/tools-codes';

const { getLocalData, removeLocalData, setLocalData } = helper;
const { WorkingEnvironment } = toolsCodes;

function created() {
	this.goTo('working-environment-surveys-new', { query: { step: 1 } });
	this.loadData();
}

function backTo() {
	if (this.number === 3 && this.rotatedCard) {
		this.rotatedCard = false;
	} else if (this.number > 1) {
		this.number -= 1;
		this.goTo('working-environment-surveys-new', { query: { step: this.number } });
	} else {
		this.goTo('working-environment');
	}
}

function cancelText() {
	if (this.number === 3 && this.rotatedCard) {
		return 'Cerrar';
	}
	return this.rotatedCard ? '' : 'Atrás';
}

function changeQuestions() {
	const selectedQuestions = this.questionsByFactor.filter(q => q.selected);
	const factorByArea = this.areas[this.active].entities.find(e => e.id === this.entitySelected.id);
	const newQuestions = selectedQuestions.map((sq) => {
		const newSq = { ...sq };
		delete newSq.disabled;
		delete newSq.selected;
		delete newSq.entityType;
		return newSq;
	});
	this.$set(factorByArea, 'questions', newQuestions);
	this.showNotification('Las preguntas han sido cambiadas correctamente.');
}

function componentName() {
	if (this.number !== 1) {
		return this.number === 2 ? 'stepTwo' : 'stepThree';
	}
	return 'stepOne';
}

function getAreaDisabled(areaValue) {
	const factorsValues = areaValue.entities.filter(entity => entity.selected);
	return factorsValues.length !== 5;
}

function getAreaValues(areaValue) {
	const factorsValues = areaValue.entities.filter(entity => entity.selected);
	return factorsValues.length * 20;
}

function getDisabled(disabledTwo) {
	this.disabledWeighing = this.$route.query.step === 2 && disabledTwo;
}

function isPage(name) {
	return this.pageName === name;
}

function isStep(step) {
	return this.number === step;
}

async function loadData() {
	const localFactorsOriginal = getLocalData('factors') || [];
	if (localFactorsOriginal.length > 0) {
		const totalQuestionsFactors = localFactorsOriginal.reduce((a, lf) => a + lf.totalWeight, 0);
		const factorsOriginal = localFactorsOriginal.map((lfo) => {
			const newLfo = { ...lfo };
			const qPercentage = Math.round((newLfo.totalWeight / totalQuestionsFactors) * 100);
			const totalWithDecimals = this.questionsQuantity * (qPercentage / 100);
			const totalFixed = totalWithDecimals.toFixed(2);
			const totalInt = totalFixed.split('.')[0];
			const totalQ = Number(totalInt);
			newLfo.totalQuestions = totalQ;
			return newLfo;
		});
		const functionReduce = () => factorsOriginal.reduce((a, f) => a + f.totalQuestions, 0);
		for (let index = 0; index < factorsOriginal.length; index += 1) {
			const reduceFo = functionReduce();
			if (this.questionsQuantity > reduceFo) {
				factorsOriginal[index].totalQuestions += 1;
			}
		}
		this.localFactors = [...factorsOriginal];
	} else {
		this.localFactors = localFactorsOriginal;
	}
	setLocalData('factors', this.localFactors);
	try {
		({ data: this.areas } = await this.$http.get(`hierarchies/${WorkingEnvironment}`));
		this.areas = this.areas.map((area) => {
			const newArea = { ...area };
			const newEntities = [...area.entities];
			newArea.entities = area.entities.map((factor) => {
				const newFactor = { ...factor };
				const factorMatch =
					this.localFactors.length > 0
						? this.localFactors.find(f => f.id === factor.id) || { selected: false }
						: { selected: false };
				newEntities.splice(0, 1);
				newFactor.criterionId = area.id;
				newFactor.criterionName = area.name;
				newFactor.selected = factorMatch.selected;
				newFactor.disabled = this.localFactors.length > 0 && !factorMatch.selected;
				newFactor.questions = [];
				newFactor.totalWeight = 0.25;
				newFactor.totalQuestions = 0;
				if (factorMatch.selected) {
					this.rowCheckSelect(newArea, newFactor);
				}
				return newFactor;
			});
			return newArea;
		});
		if (this.$route.query.step === 3) {
			this.loadQuestionsRandom();
		}
	} catch (err) {
		if (err.status === 500) {
			this.showGenericError();
		}
	}
}

async function loadQuestionsRandom() {
	const body = {
		factors: getLocalData('factors').map((f) => {
			const newF = {
				factorId: f.id,
				totalQuestions: f.totalQuestions,
			};
			return newF;
		}),
	};
	try {
		({ data: this.questions } = await this.$http.post('questions/random', body));
		this.areas = this.areas.map((area) => {
			const newArea = { ...area };
			newArea.entities = area.entities.map((entity) => {
				const newEntity = { ...entity };
				const questionsByFactor = this.questions.find(q => q.name === entity.name);
				newEntity.questions = questionsByFactor ? [...questionsByFactor.questions] : [];
				return newEntity;
			});
			return newArea;
		});
	} catch (err) {
		if (err.status === 500) {
			this.showGenericError();
		}
	}
}

function onChange() {
	const questionsSelected = this.questionsByFactor.filter(q => q.selected);
	this.questionsByFactor = this.questionsByFactor.map((q) => {
		const newQ = { ...q };
		newQ.disabled = !q.selected && questionsSelected.length === this.quantity;
		return newQ;
	});
	this.disabledQuestions = this.questionsByFactor.every(qbf => !qbf.disabled);
}

function onChangeTab(tab) {
	this.number = tab;
	this.goTo('working-environment-surveys-new', { query: { step: this.number } });
}

function rowCheckSelect(areaVal, factorEntity) {
	this.rotatedCard = false;
	this.factorSelected = {};
	this.kindEntityId = factorEntity.kindEntityId;
	const kindEntityDataIndex = areaVal.kindEntityData.findIndex(
		k => k.id === factorEntity.kindEntityId,
	);
	const graphicSide = kindEntityDataIndex === 0 ? 'Left' : 'Right';
	if (this.areas[0].id === areaVal.id) {
		this[`graphicValue${graphicSide}`] = factorEntity.selected
			? (this[`graphicValue${graphicSide}`] += 1)
			: (this[`graphicValue${graphicSide}`] -= 1);
	} else {
		this[`graphicValue${graphicSide}Second`] = factorEntity.selected
			? (this[`graphicValue${graphicSide}Second`] += 1)
			: (this[`graphicValue${graphicSide}Second`] -= 1);
	}
	const allFactors = this.areas.reduce((acum, a) => acum.concat(a.entities), []);
	this.localFactors =
		getLocalData('factors') &&
		getLocalData('factors').length === 10 &&
		allFactors.filter(af => af.selected).length !== 10
			? this.localFactors
			: allFactors.filter(af => af.selected);
	this.selectFactors = this.localFactors.length < 10;
}

function $route(value) {
	if (value.name === 'working-environment-surveys-new') {
		this.showNumber = true;
		if (value.query.step === 1) {
			this.disabledWeighing = false;
			this.selectFactors = this.localFactors.length < 10;
		} else if (value.query.step === 2) {
			this.newAreas = this.areas.map((area) => {
				const newArea = { ...area };
				const newFactors = this.localFactors.filter(val => val.toolId === area.id);
				newArea.entities = newFactors.map((localFactor) => {
					const newLocalFactor = { ...localFactor };
					newLocalFactor.value = Math.round(localFactor.value);
					return newLocalFactor;
				});
				return newArea;
			});
		} else if (value.query.step === 3) {
			this.title = 'CLIMA LABORAL';
			this.showNumber = false;
			this.loadQuestionsRandom();
		}
	}
}

function selectFactor(factorItem) {
	this.factorSelected = { ...factorItem };
	const area = this.areas.find(a => a.id === factorItem.toolId) || {};
	this.rotatedCard = true;
	this.selectFactors = false;
	this.title = area.name;
}

async function showQuestions({ id, name }) {
	const entity = this.localFactors.find(lf => lf.id === id);
	this.entitySelected = this.areas[this.active].entities.find(e => e.id === id);
	this.nameFactor = name;
	this.showNumber = false;
	this.titleQuestions = `Factor ${name}`;
	this.showStep = false;
	this.quantity = entity.totalQuestions;
	try {
		({ data: this.questionsByFactor } = await this.$http.get(`hierarchies/children/${id}`));
		this.questionsByFactor = this.questionsByFactor.map((q) => {
			const newQ = { ...q };
			const qSelected = this.entitySelected.questions.find(eq => eq.id === q.id);
			newQ.selected = !!qSelected;
			newQ.disabled = !qSelected;
			return newQ;
		});
		this.disabledQuestions = this.questionsByFactor.every(qbf => !qbf.disabled);
	} catch (err) {
		if (err.status === 500) {
			this.showGenericError();
		}
	} finally {
		this.rotatedCard = true;
	}
}

function successAction() {
	if (!this.rotatedCard) {
		if (this.number === 3) {
			this.dialog = true;
		} else {
			if (this.number === 1) {
				const newLocalFactors = [...this.localFactors];
				this.localFactors = this.localFactors.map((factorL, factorIndex) => {
					const newFactorL = { ...factorL };
					newFactorL.relatedFactorWeights = newLocalFactors.reduce(
						(acum, relatedFactor, relatedIndex) => {
							if (factorIndex !== relatedIndex) {
								const newRelatedFactor = {
									criterionId: relatedFactor.criterionId,
									id: relatedFactor.id,
									name: relatedFactor.name,
									urlImage: relatedFactor.urlImage,
									weight: 0.75,
								};
								acum.push(newRelatedFactor);
							}
							return acum;
						},
						[],
					);
					return newFactorL;
				});
				setLocalData('factors', this.localFactors);
				setLocalData('graphicValueLeft', this.graphicValueLeft);
				setLocalData('graphicValueLeftSecond', this.graphicValueLeftSecond);
				setLocalData('graphicValueRight', this.graphicValueRight);
				setLocalData('graphicValueRightSecond', this.graphicValueRightSecond);
				if (this.localFactors.some(lf => lf.totalQuestions <= 0)) {
					removeLocalData('weighingByFactor');
				}
			} else if (this.number === 2) {
				setLocalData('factors', this.localFactors);
			}
			this.number += 1;
		}
	} else {
		if (this.number === 3) {
			this.changeQuestions();
		}
		this.rotatedCard = false;
		this.factorSelected = {};
		this.selectFactors = this.localFactors.length < 10;
	}
}

function successText() {
	if (this.number === 3 && this.rotatedCard) {
		return 'Guardar';
	}
	return this.rotatedCard ? 'Cerrar' : 'Siguiente';
}

function onActive(value) {
	this.active = value;
}

function closeModal(val) {
	this.dialog = val;
}

async function createSurvey() {
	if (this.isCreatingSurvey) return;
	this.isCreatingSurvey = true;
	const body = {
		name: this.nameSurvey,
		toolCode: WorkingEnvironment,
		factorsWeight: this.localFactors.map((lf) => {
			// Buscar el factor en this.areas que contiene las preguntas modificadas por el usuario
			const factorInArea = this.areas.reduce((found, area) => {
				if (found) return found;
				return area.entities.find(entity => entity.id === lf.id);
			}, null);

			const newLf = {
				id: lf.id,
				name: lf.name,
				criterionId: lf.criterionId,
				criterionName: lf.criterionName,
				totalWeight: lf.totalWeight,
				totalQuestions: lf.totalQuestions,
				relatedFactorWeights: lf.relatedFactorWeights,
				questions:
					factorInArea && factorInArea.questions ? factorInArea.questions.map(q => q.id) : [],
			};
			return newLf;
		}),
	};
	if (this.$userInfo && this.$userInfo.company &&
		this.$userInfo.company.additionalInformation &&
		this.$userInfo.company.additionalInformation.answerId) {
		body.answerId = this.$userInfo.company.additionalInformation.answerId;
	}
	try {
		const { data: poll } = await this.$http.post('polls', body);
		this.showNotification('Encuesta creada correctamente.');
		removeLocalData('factors');
		removeLocalData('graphicValueLeft');
		removeLocalData('graphicValueLeftSecond');
		removeLocalData('graphicValueRight');
		removeLocalData('graphicValueRightSecond');
		removeLocalData('weighingByFactor');
		const params = { toolCode: WorkingEnvironment, pollId: poll.id };
		this.goTo('new-diagnosis', { params });
	} catch (err) {
		if (err.status === 500) {
			this.showGenericError();
		}
	} finally {
		this.isCreatingSurvey = false;
	}
}

function quantitySelected(value) {
	this.questionsQuantity = value;
	this.loadData();
}

function data() {
	return {
		active: 0,
		areas: [],
		dialog: false,
		disabledQuestions: false,
		disabledWeighing: false,
		entitySelected: {},
		factorSelected: {},
		graphicValueLeft: 0,
		graphicValueLeftSecond: 0,
		graphicValueRight: 0,
		graphicValueRightSecond: 0,
		items: [
			{
				name: 'Escoge 5 factores',
				number: 1,
			},
			{
				name: 'Ponderación de Factores',
				number: 2,
			},
			{
				name: 'Preguntas',
				number: 3,
			},
		],
		kindEntityId: 0,
		localFactors: [],
		mainFactorValue: 0,
		nameFactor: '',
		nameSurvey: '',
		newAreas: [],
		number: 1,
		pageName: '',
		quantity: 0,
		questions: [],
		questionsByFactor: [],
		questionsQuantity: 0,
		rotatedCard: false,
		secondFactorAreaId: null,
		secondFactorId: null,
		selectFactors: true,
		showNumber: true,
		showStep: true,
		title: 'ESCOGE 5 FACTORES POR CRITERIO',
		titleQuestions: '',
		isCreatingSurvey: false,
	};
}

export default {
	name: 'page-surveys-new',
	components: {
		cardDetail,
		componentWeighing,
		graphicComponent,
		graphicFactors,
		openModal,
		questionsDetail,
		questionsList,
		stepOne,
		stepThree,
		stepTwo,
		tabs,
	},
	computed: {
		cancelText,
		componentName,
		successText,
	},
	created,
	data,
	methods: {
		backTo,
		changeQuestions,
		closeModal,
		createSurvey,
		getAreaDisabled,
		getAreaValues,
		getDisabled,
		isPage,
		isStep,
		loadData,
		loadQuestionsRandom,
		onActive,
		onChange,
		onChangeTab,
		quantitySelected,
		rowCheckSelect,
		selectFactor,
		showQuestions,
		successAction,
	},
	watch: {
		$route,
	},
};
</script>
<style lang="scss" scoped>
.new-survey-label {
	color: color(tooltip-gray);
}

.message-error-container {
	position: relative;
}

.message-error {
	bottom: -15px;
	font-family: font(regular);
	font-size: size(small11);
	position: absolute;
	width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/pages/working-environment/page-surveys-new.vue